import { useCallback, useState } from 'react'
import type { ConexaoDto, ContagemPorEscopo } from '@crm/shared'
import { BotaoIcone } from '../../componentes/BotaoIcone'
import { useFecharAoClicarFora } from '../../componentes/fechar-fora'
import { Flutuante } from '../../componentes/Flutuante'
import { IconeBuscar, IconeMais, IconeSetaBaixo, IconeWhatsApp } from '../../componentes/icones'
import { juntar } from '../../componentes/ui'
import { GatilhoDeFiltros, type ValoresDoFiltro } from './FiltrosDaLista'

export type EscopoDaLista = 'todas' | 'minhas' | 'fila'
export type StatusDaLista = 'aberto' | 'fechado' | 'todas'

const ROTULO_DO_STATUS: Record<StatusDaLista, string> = {
  aberto: 'Abertos',
  fechado: 'Encerrados',
  // "Todos" no masculino de propósito: concorda com abertos e encerrados, e
  // não vira um segundo "Todas" colado no recorte de fila ao lado.
  todas: 'Todos',
}

/**
 * O topo da coluna de conversas: recorte, situação, busca, número, filtros e
 * começar conversa.
 *
 * Tudo com a mesma altura e o mesmo desenho. Antes eram quatro estilos
 * diferentes empilhados em duas linhas: abas de 28px com fundo cinza, um
 * seletor cinza de letra menor, uma busca de 32px com anel e ícones de 28px.
 * Cada peça parecia de um sistema, e a coluna começava com uma colagem.
 *
 * A ordem segue a pergunta que cada controle responde: primeiro **quais**
 * conversas (recorte e situação), depois **qual** delas (busca), e por fim o
 * que é de outra natureza (o número da casa, os filtros, iniciar conversa).
 */
export function BarraDaLista({
  escopo,
  aoTrocarEscopo,
  contagens,
  status,
  aoTrocarStatus,
  busca,
  aoBuscar,
  conexoes,
  conexaoAtual,
  aoTrocarConexao,
  filtros,
  filtrosAbertos,
  aoAlternarFiltros,
  aoLimparFiltros,
  aoIniciarConversa,
  escopos,
}: {
  escopo: EscopoDaLista
  aoTrocarEscopo: (e: EscopoDaLista) => void
  contagens: ContagemPorEscopo | undefined
  status: StatusDaLista
  aoTrocarStatus: (s: StatusDaLista) => void
  busca: string
  aoBuscar: (b: string) => void
  conexoes: ConexaoDto[]
  conexaoAtual: ConexaoDto | null
  aoTrocarConexao: (id: string) => void
  filtros: ValoresDoFiltro
  filtrosAbertos: boolean
  aoAlternarFiltros: () => void
  aoLimparFiltros: () => void
  /** Sem a permissão de iniciar conversa, o botão + não aparece (ADR 0038). */
  aoIniciarConversa?: () => void
  /** As abas que a pessoa pode abrir: Fila e Todas têm permissão própria. */
  escopos: EscopoDaLista[]
}) {
  const [seletorAberto, setSeletorAberto] = useState(false)
  const fecharSeletor = useCallback(() => setSeletorAberto(false), [])
  const seletor = useFecharAoClicarFora<HTMLDivElement>(seletorAberto, fecharSeletor)

  return (
    <div className="border-b border-tinta-200">
      <div className="flex items-center gap-1 px-2 pt-2">
        <div className="flex h-8 flex-1 rounded-full bg-tinta-100 p-0.5 text-xs">
          {/*
            A ordem é a do dia de trabalho: primeiro o que é meu, depois o que
            está esperando alguém pegar, e por último a visão geral.
          */}
          {(['minhas', 'fila', 'todas'] as const)
            .filter((e) => escopos.includes(e))
            .map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => aoTrocarEscopo(e)}
                aria-pressed={escopo === e}
                className={juntar(
                  /*
                  `px-0.5` e não `px-1`: as três abas dividem a largura da
                  coluna, e "Minhas" com um contador de dois dígitos estourava
                  o terço dela. Dois pixels de recuo a menos resolvem o encaixe
                  sem tirar área de clique.
                */
                  'flex flex-1 items-center justify-center gap-1 rounded-full px-0.5 font-semibold capitalize transition',
                  escopo === e
                    ? 'bg-superficie text-tinta-900 shadow-suave'
                    : 'text-tinta-500 hover:text-tinta-900',
                )}
              >
                {e}
                {/*
                O número ao lado do nome, e não só na aba aberta: conversa nova
                de contato sem dono cai na fila sozinha, e sem contador ninguém
                percebe até abrir a aba.

                Zero não vira "0": aba vazia com um zero pintado pede atenção
                para dizer que não há nada.
              */}
                {contagens?.[e] ? (
                  <span
                    className={juntar(
                      'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[11px] font-medium tabular-nums',
                      escopo === e ? 'bg-marca text-white' : 'bg-tinta-200 text-tinta-600',
                    )}
                  >
                    {contagens[e] > 99 ? '99+' : contagens[e]}
                  </span>
                ) : null}
              </button>
            ))}
        </div>

        <select
          value={status}
          onChange={(e) => aoTrocarStatus(e.target.value as StatusDaLista)}
          className="h-8 shrink-0 rounded-md bg-superficie px-1.5 text-xs text-tinta-700 ring-1 ring-tinta-200 outline-none focus:ring-2 focus:ring-marca"
          title="Situação das conversas listadas"
        >
          {(Object.keys(ROTULO_DO_STATUS) as StatusDaLista[]).map((s) => (
            <option key={s} value={s}>
              {ROTULO_DO_STATUS[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1 px-2 py-2">
        <label className="relative flex-1">
          <span className="sr-only">Buscar conversa</span>
          <IconeBuscar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-400" />
          <input
            value={busca}
            onChange={(e) => aoBuscar(e.target.value)}
            placeholder="Buscar conversa"
            className="h-8 w-full rounded-md bg-superficie pl-8 pr-2 text-sm ring-1 ring-tinta-200 outline-none focus:ring-2 focus:ring-marca"
          />
        </label>

        {/*
          O número inteiro tomava uma linha da coluna para mostrar algo que
          quase nunca muda: a maioria opera com um número só. Recolhido no
          ícone, fica à vista o que interessa acompanhar, que é a bolinha de
          conectado ou fora do ar.
        */}
        <div ref={seletor} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setSeletorAberto((a) => !a)}
            aria-label={`Número: ${conexaoAtual?.numero}, ${conexaoAtual?.status}`}
            title={`${conexaoAtual?.numero} · ${conexaoAtual?.nome} · ${
              conexaoAtual?.status === 'conectado' ? 'conectado' : 'fora do ar'
            }`}
            className="relative flex h-8 items-center gap-0.5 rounded-md px-1.5 text-tinta-600 transition hover:bg-tinta-100 hover:text-tinta-900"
          >
            <IconeWhatsApp className="h-4 w-4 text-green-600" />
            <span
              className={juntar(
                'absolute right-2.5 top-1 h-2 w-2 rounded-full ring-2 ring-superficie',
                conexaoAtual?.status === 'conectado' ? 'bg-green-500' : 'bg-red-500',
              )}
            />
            <IconeSetaBaixo className="h-3 w-3" />
          </button>

          {/*
            Flutua fora da coluna da lista, que corta o que passa da borda
            dela: abria rente à conversa aberta e perdia a ponta da direita.
          */}
          <Flutuante
            ancora={seletor}
            aberto={seletorAberto}
            aoFechar={fecharSeletor}
            largura={256}
            distancia={4}
            className="overflow-y-auto rounded-xl bg-superficie py-1 shadow-media ring-1 ring-tinta-200"
          >
            <ul>
              {conexoes.map((conexao) => (
                <li key={conexao.id}>
                  <button
                    type="button"
                    onClick={() => {
                      aoTrocarConexao(conexao.id)
                      setSeletorAberto(false)
                    }}
                    className={juntar(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-tinta-50',
                      conexao.id === conexaoAtual?.id && 'bg-marca/5',
                    )}
                  >
                    <IconeWhatsApp className="h-4 w-4 shrink-0 text-green-600" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{conexao.numero}</span>
                      <span className="block truncate text-xs text-tinta-400">
                        {conexao.nome}
                        {conexao.somenteLeitura ? ' · só recebe' : ''}
                      </span>
                    </span>
                    <span
                      className={juntar(
                        'h-2 w-2 shrink-0 rounded-full',
                        conexao.status === 'conectado' ? 'bg-green-500' : 'bg-red-500',
                      )}
                      title={conexao.status}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </Flutuante>
        </div>

        <GatilhoDeFiltros
          valores={filtros}
          aberto={filtrosAbertos}
          aoAlternar={aoAlternarFiltros}
          aoLimpar={aoLimparFiltros}
        />

        {/*
          Começar conversa fica junto da busca de propósito: são as duas formas
          de chegar a uma pessoa, achar quem já está aqui ou chamar quem ainda
          não está.
        */}
        {aoIniciarConversa ? (
          <BotaoIcone
            rotulo="Iniciar conversa com um número"
            dicaAlinhada="direita"
            onClick={aoIniciarConversa}
          >
            <IconeMais className="h-4 w-4" />
          </BotaoIcone>
        ) : null}
      </div>
    </div>
  )
}
